import { useState } from 'react';

import { api } from '../lib/api';
import { Alert, Badge, Card, EnvPill, Field, FieldGroup, PageHead, SecretReveal, formatTime, useLoader } from '../components/ui';

const SCOPES = [
  'full_access',
  'emails:send',
  'emails:read',
  'domains:manage',
  'api_keys:manage',
  'templates:manage',
  'suppressions:manage',
  'webhooks:manage',
];

export function ApiKeys() {
  const { data, error, loading, reload } = useLoader(() => api.apiKeys.list());
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('live');
  const [scopes, setScopes] = useState<string[]>(['full_access']);
  const [created, setCreated] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function toggleScope(scope: string) {
    setScopes((current) => {
      // full_access supersedes everything, so selecting it clears the rest rather
      // than sending a contradictory list.
      if (scope === 'full_access') return ['full_access'];
      const without = current.filter((item) => item !== 'full_access');
      return without.includes(scope)
        ? without.filter((item) => item !== scope)
        : [...without, scope];
    });
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const key = await api.apiKeys.create({ name: name || 'default', environment, scopes });
      setCreated(key.key ?? null);
      setCreating(false);
      setName('');
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create key');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="API keys"
        subtitle="Also used as the SMTP password — there is no separate SMTP credential, so revoking a key closes both doors."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(!creating)}
          >
            {creating ? 'Cancel' : 'Create key'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {created && (
        <Card title="Your new key">
          <SecretReveal label="API key" value={created} />
          <button type="button" className="btn btn-sm" onClick={() => setCreated(null)}>
            Done
          </button>
        </Card>
      )}

      {creating && (
        <Card title="Create an API key">
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={create}>
            <Field id="key-name" label="Name">
              {(props) => (
                <input
                  {...props}
                  placeholder="production-api"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>

            <Field id="key-env" label="Environment">
              {(props) => (
                <select
                  {...props}
                  value={environment}
                  onChange={(event) => setEnvironment(event.target.value)}
                >
                  <option value="live">Live — sends real mail</option>
                  <option value="test">Test — full pipeline, no mail, no quota</option>
                </select>
              )}
            </Field>

            <FieldGroup
              id="key-scopes"
              label="Scopes"
              hint="Narrow scopes limit the damage a leaked key can do. A key for a webhook receiver does not need to be able to send."
            >
              <div className="checkbox-grid">
                {SCOPES.map((scope) => (
                  <label className="checkbox-row" key={scope}>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                    />
                    <span className="mono">{scope}</span>
                  </label>
                ))}
              </div>
            </FieldGroup>

            <button className="btn btn-primary" disabled={busy || scopes.length === 0}>
              {busy ? 'Creating…' : 'Create key'}
            </button>
          </form>
        </Card>
      )}

      <Card tight title="Keys">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Env</th>
                <th>Scopes</th>
                <th>Last used</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((key) => (
                <tr key={key.id}>
                  <td>{key.name}</td>
                  <td className="mono">{key.key_prefix}…</td>
                  <td>
                    <EnvPill environment={key.environment} />
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      {key.scopes.map((scope) => (
                        <Badge kind="muted" key={scope}>
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {key.last_used_at ? formatTime(key.last_used_at) : 'never'}
                  </td>
                  <td>
                    {key.revoked_at ? (
                      <Badge kind="danger">revoked</Badge>
                    ) : (
                      <Badge kind="ok">active</Badge>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!key.revoked_at && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={async () => {
                          if (!window.confirm(`Revoke "${key.name}"? This cannot be undone.`)) {
                            return;
                          }
                          await api.apiKeys.revoke(key.id);
                          reload();
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data ?? []).length === 0 && (
          <div className="empty">{loading ? 'Loading…' : 'No keys yet.'}</div>
        )}
      </Card>

      <Card>
        <div className="tile-label">Rotating a key</div>
        <p className="subtitle" style={{ marginTop: 6 }}>
          Create the replacement, deploy it, then revoke the old one. Both are valid in
          between, so there is no window where your traffic has to stop. Revoked keys keep
          their row so past sends and audit entries keep a resolvable actor.
        </p>
      </Card>
    </>
  );
}
